import {
  CONFIGURATION_STATUS_TYPE,
  FEATURE_CONFIG_SELECTION_TYPE,
  FEATURE_CONFIG_SELECTION_TYPE_TYPE,
  LEVEL,
  LEVEL_TYPE,
  PROCESS,
  PROCESS_TYPE,
  PROJECT_TYPE,
} from 'src/shared/constants/constants'
import { Feature_Group_Config, Feature_Type_Config } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Option } from 'src/shared/form/control'
import { useCallback, useMemo, useState } from 'react'
import { CheckboxData, CheckboxGroupField } from 'src/shared/form/control/CheckboxGroupField'
import { Form } from 'react-final-form'
import { Alert, Divider, Grid2, Stack, Typography } from '@mui/material'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { required } from 'src/shared/form/validation/validators'
import { TextField } from 'src/shared/form/control/TextField'
import { useUserLocale } from 'src/user/UserContext'
import {
  KapFactsheetLevelRequiresKpi,
  mapFeatureGroupsToOptions,
} from 'src/screens/administration/configuration-management/features-configuration/features/edit/FeaturesConfigurationFeaturesFormUtils'
import styled from 'styled-components'
import { COLORS } from 'src/theme/colors'
import { OnChange } from 'react-final-form-listeners'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import {
  getConfigStatusOptions,
  getLevelOptions,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import {
  getFeaturesConfigProcessOptions,
  mapFeatureTypesToOptions,
} from 'src/screens/administration/configuration-management/features-configuration/FeaturesConfigurationUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'

const DividerStyled = styled(Divider)(({ theme }) => ({
  height: '2px',
  backgroundColor: theme.palette.divider,
}))

export interface FeaturesConfigFormValues {
  enName: string
  deName: string
  frName: string
  itName: string
  enDescription: string
  deDescription: string
  frDescription: string
  itDescription: string
  status: CONFIGURATION_STATUS_TYPE
  selectionType: FEATURE_CONFIG_SELECTION_TYPE_TYPE
  featureTypeId: number | null
  featureGroupId: number | null
  processAlloc: PROCESS_TYPE[]
  pfKapRequiresKpi: string | null
  pfPgvRequiresKpi: string | null
  kapFactsheetLevels: LEVEL_TYPE[]
  kapFactsheetLevelRequiresKpi: KapFactsheetLevelRequiresKpi
}

interface FeaturesConfigurationFeaturesFormProps {
  mode: 'EDIT' | 'CREATE'
  onSubmit: (values: FeaturesConfigFormValues) => Promise<void>
  initialValues: FeaturesConfigFormValues
  featureTypes: Feature_Type_Config[]
  featureGroups: Feature_Group_Config[]
  formDirty?: boolean
}

export const FeaturesConfigurationFeaturesForm = ({
  mode,
  onSubmit,
  initialValues,
  featureTypes,
  featureGroups,
  formDirty,
}: FeaturesConfigurationFeaturesFormProps) => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const decorators = createDecorators()

  const [selectedFeatureTypeId, setSelectedFeatureTypeId] = useState<number | null>(null)

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const selectionTypeOptions: Option[] = useMemo(
    () => [
      {
        label: getMessage('label.configuration.features.selection.type.checkbox'),
        value: FEATURE_CONFIG_SELECTION_TYPE.SELECT,
      },
      {
        label: getMessage('label.configuration.features.selection.type.text'),
        value: FEATURE_CONFIG_SELECTION_TYPE.TEXT,
      },
    ],
    [getMessage],
  )

  const processAllocOptions: CheckboxData[] = useMemo(() => getFeaturesConfigProcessOptions(getMessage), [getMessage])

  const getRequiresKpiOptions = useCallback(
    (trueValue: PROCESS_TYPE | LEVEL_TYPE): Option[] => [
      { label: getMessage('label.configuration.features.requires.kpi.no'), value: null },
      { label: getMessage('label.configuration.features.requires.kpi.yes'), value: trueValue },
    ],
    [getMessage],
  )

  const levelsCheckboxOptions: CheckboxData[] = useMemo(() => getLevelOptions(getMessage), [getMessage])

  const featureTypesOptions: Option[] = useMemo(
    () => mapFeatureTypesToOptions(featureTypes, getMessage, locale),
    [featureTypes, getMessage, locale],
  )

  const featureGroupOptions: Option[] = useMemo(() => {
    let filteredGroupsByType: Feature_Group_Config[]
    if (mode === 'EDIT') {
      // the feature group of the feature_config we are editing is the only option that appears.
      // the feature_group cannot be changed.
      filteredGroupsByType = featureGroups.filter((fgc) => fgc.id === initialValues.featureGroupId)
    } else {
      // only show the feature_groups that are related to the currently selected feature type
      filteredGroupsByType = featureGroups.filter((fgc) => fgc.feature_type_id === selectedFeatureTypeId)
    }

    return mapFeatureGroupsToOptions(filteredGroupsByType, getMessage, locale)
  }, [featureGroups, initialValues.featureGroupId, mode, selectedFeatureTypeId, getMessage, locale])

  return (
    <Stack spacing={2}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<FeaturesConfigFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        decorators={decorators}
        mutators={{
          clearRequiresKpiInProjectsAndLevelsOnProcessChange: ([currProcesses], state, utils) => {
            if (!currProcesses.includes(PROCESS.PF_KAP)) {
              utils.changeValue(state, 'pfKapRequiresKpi', () => null)
              setTimeout(() => {
                utils.resetFieldState('pfKapRequiresKpi')
              }, 0)
            }
            if (!currProcesses.includes(PROCESS.PF_PGV)) {
              utils.changeValue(state, 'pfPgvRequiresKpi', () => null)
              setTimeout(() => {
                utils.resetFieldState('pfPgvRequiresKpi')
              }, 0)
            }
            if (!currProcesses.includes(PROCESS.KAP)) {
              utils.changeValue(state, 'kapFactsheetLevels', () => [])
              setTimeout(() => {
                utils.resetFieldState('kapFactsheetLevels')
              }, 0)
            }
          },
          clearKapFactsheetRequiresKpiOnLevelsChange: ([currLevels], state, utils) => {
            Object.values(LEVEL).forEach((level) => {
              if (!currLevels.includes(level)) {
                utils.changeValue(state, `kapFactsheetLevelRequiresKpi.${level}`, () => null)
                setTimeout(() => {
                  utils.resetFieldState(`kapFactsheetLevelRequiresKpi.${level}`)
                }, 0)
              }
            })
          },
          clearFeatureGroupField: (_, state, utils) => {
            utils.changeValue(state, 'featureGroupId', () => null)
            setTimeout(() => {
              utils.resetFieldState('featureGroupId')
            }, 0)
          },
        }}
        render={({ handleSubmit, form, values }) => {
          return (
            <form id="feature-config-form" onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={selectionTypeOptions}
                      name="selectionType"
                      label={getMessage('label.configuration.features.selection.type')}
                      disabled={mode === 'EDIT'}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.name')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField name="deName" label="DE" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enName" label="EN" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frName" label="FR" required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itName" label="IT" required validate={required()} />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">
                  {getMessage('label.configuration.features.type.tooltip.description')}
                </Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField name="deDescription" label="DE" />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enDescription" label="EN" />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frDescription" label="FR" />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itDescription" label="IT" />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.configuration.grid.allocation')}</Typography>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={featureTypesOptions}
                      name="featureTypeId"
                      label={getMessage('label.feature.type')}
                      getOptionKey={(option) => option.value?.toString() ?? ''}
                      disabled={mode === 'EDIT'}
                      disableClearable
                      required
                      validate={required()}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={featureGroupOptions}
                      name="featureGroupId"
                      label={getMessage('label.feature.group')}
                      getOptionKey={(option) => option.value?.toString() ?? ''}
                      disabled={mode === 'EDIT' || values.featureTypeId === null}
                    />
                  </Grid2>
                </Grid2>

                <Grid2 container columnSpacing={5}>
                  <CheckboxGroupField
                    name="processAlloc"
                    color="primary"
                    label={
                      <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                        {getMessage('label.process')}
                      </Typography>
                    }
                    data={processAllocOptions}
                    withDividersBetween
                    formControlProps={{ style: { marginTop: 0 } }}
                    disabled={mode === 'EDIT'}
                    required
                    validate={required()}
                    render={(item, _) => {
                      const disabledLevelsAndRequiresKpi =
                        mode === 'EDIT' || !values.processAlloc.includes(item.value as PROCESS_TYPE)
                      const validateLevels = values.processAlloc.includes(item.value as PROJECT_TYPE)
                        ? required()
                        : undefined
                      switch (item.value) {
                        case PROCESS.PF_KAP:
                          return (
                            <Stack key="pfKapCheckbox" direction="row" width="80%" paddingTop={2}>
                              <DividerStyled />
                              <AutoCompleteField
                                options={getRequiresKpiOptions(PROCESS.PF_KAP)}
                                name="pfKapRequiresKpi"
                                label={getMessage('label.configuration.features.requires.kpi')}
                                disabled={disabledLevelsAndRequiresKpi}
                                disableClearable
                                style={{ width: '50%' }}
                              />
                            </Stack>
                          )
                        case PROCESS.PF_PGV:
                          return (
                            <Stack key="pfPgvCheckbox" direction="row" width="80%" paddingTop={2}>
                              <AutoCompleteField
                                options={getRequiresKpiOptions(PROCESS.PF_PGV)}
                                name="pfPgvRequiresKpi"
                                label={getMessage('label.configuration.features.requires.kpi')}
                                disabled={disabledLevelsAndRequiresKpi}
                                disableClearable
                                style={{ width: '50%' }}
                              />
                            </Stack>
                          )
                        case PROCESS.KAP:
                          return (
                            <Stack key="kapFactsheetCheckbox" direction="row" width="80%" paddingTop={2}>
                              <CheckboxGroupField
                                key={`${validateLevels}`}
                                name="kapFactsheetLevels"
                                data={levelsCheckboxOptions}
                                color="primary"
                                formLabelProps={{
                                  style: {
                                    color: disabledLevelsAndRequiresKpi ? COLORS.text.disabled : COLORS.text.primary,
                                  },
                                }}
                                label={getMessage('label.levels')}
                                disabled={disabledLevelsAndRequiresKpi}
                                validate={validateLevels}
                                render={(item, _) => {
                                  const disabledKapFactsheetRequiresKpi = !values.kapFactsheetLevels.includes(
                                    item.value as LEVEL_TYPE,
                                  )
                                  return (
                                    <Stack key={`level-${item.value}-checkbox`} width="80%" paddingTop={2}>
                                      <AutoCompleteField
                                        options={getRequiresKpiOptions(item.value as LEVEL_TYPE)}
                                        name={`kapFactsheetLevelRequiresKpi.${item.value}`}
                                        label={getMessage('label.configuration.features.requires.kpi')}
                                        disabled={disabledLevelsAndRequiresKpi || disabledKapFactsheetRequiresKpi}
                                        disableClearable
                                        style={{ width: '50%' }}
                                      />
                                    </Stack>
                                  )
                                }}
                              />
                            </Stack>
                          )
                        default:
                          return <></>
                      }
                    }}
                  />
                </Grid2>
                <OnChange name="processAlloc">
                  {(currProcesses, _) => {
                    form.mutators.clearRequiresKpiInProjectsAndLevelsOnProcessChange(currProcesses)
                  }}
                </OnChange>
                <OnChange name="kapFactsheetLevels">
                  {(currLevels, _) => {
                    form.mutators.clearKapFactsheetRequiresKpiOnLevelsChange(currLevels)
                  }}
                </OnChange>
                <OnChange name="featureTypeId">
                  {(currFeatureTypeId: number, _) => {
                    if (mode === 'CREATE') {
                      setSelectedFeatureTypeId(currFeatureTypeId)
                      form.mutators.clearFeatureGroupField()
                    }
                  }}
                </OnChange>
                <DirtyFormSpy gridDirty={formDirty} />
              </Stack>
            </form>
          )
        }}
      />
    </Stack>
  )
}
